// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';

/** Corporate-holdings enrichment via the resource-server /api/cro relay (BFF-proxied). */

export interface CroCandidate {
  providerPersonId: string;
  personKey?: string;
  name: string;
  dob?: string;
  address?: string;
  county?: string;
  selected: boolean;
}

export interface CroSearchResult {
  id: string;
  queryId?: string;
  status: string;
  servedFromCache: boolean;
  offset: number;
  hasMore: boolean;
  candidateCount: number;
  candidates: CroCandidate[];
}

export interface CroDirectorship {
  companyNumber: string;
  companyName: string;
  role?: string;
  companyStatus?: string;
  appointedOn?: string;
  resignedOn?: string;
}

export interface CroShareholding {
  companyNumber: string;
  companyName: string;
  percent?: number;
}

export interface CroBusinessName {
  businessNumber: string;
  name: string;
}

export interface CroPersonHoldings {
  personKey: string;
  name: string;
  directorships: CroDirectorship[];
  shareholdings: CroShareholding[];
  previousShareholdings: CroShareholding[];
  businessNames: CroBusinessName[];
}

export interface CroHoldings {
  clientProfileId: string;
  persons: CroPersonHoldings[];
}

export interface CroCompanyHolding {
  companyKey: string;
  companyNumber: string;
  name: string;
  entityType: 'COMPANY' | 'BUSINESS';
  status?: string;
  roles: string[];
  sharePercent?: number;
  netAssets?: number;
  stakeValue?: number;
  currencyCode?: string;
}

export interface CroProfileHoldings {
  profileId: string;
  persons: CroPersonHoldings[];
  companies: CroCompanyHolding[];
}

export interface CroCompanyFinancial {
  yearEnd?: string;
  turnover?: number;
  netAssets?: number;
  cash?: number;
  currentAssets?: number;
  currentLiabilities?: number;
  groupAccounts?: boolean;
}

export interface CroCompanyDetail {
  companyKey: string;
  companyNumber: string;
  name: string;
  entityType: string;
  statusCode?: number;
  status?: string;
  type?: string;
  registeredOn?: string;
  dissolvedOn?: string;
  address?: string;
  county?: string;
  eircode?: string;
  naceCode?: string;
  officers: { providerPersonId: string; name: string; role?: string }[];
  shareholders: { name: string; percent?: number; type?: string }[];
  financials: CroCompanyFinancial[];
  stale: boolean;
}

export interface CroMatch {
  personKey: string;
  providerPersonId: string;
  clientProfileId: string;
  status: string;
  confirmedAt?: string;
}

export interface CroSearchCriteria {
  firstName: string;
  lastName: string;
  birthDay?: string;
  birthMonth?: string;
  birthYear?: string;
  address?: string;
  county?: string;
  eircode?: string;
  clientProfileId?: string;
  offset?: number;
}

export const croApi = {
  getHoldings: async (clientProfileId: string): Promise<CroHoldings> =>
    (await api.get(`/cro/clients/${clientProfileId}/holdings`)).data,

  /** Corporate holdings aggregated across a relationship's clients (dashboard card). */
  getProfileHoldings: async (profileId: string): Promise<CroProfileHoldings> =>
    (await api.get(`/cro/profiles/${profileId}/holdings`)).data,

  /** Full company/business detail for the Details drawer (cache-first). */
  getCompany: async (companyKey: string, name?: string, entityType?: string): Promise<CroCompanyDetail> =>
    (await api.get(`/cro/companies/${encodeURIComponent(companyKey)}`, { params: { name, entityType } })).data,

  search: async (criteria: CroSearchCriteria): Promise<CroSearchResult> =>
    (await api.post('/cro/searches', criteria)).data,

  confirm: async (
    searchId: string,
    clientProfileId: string,
    confirm: string[],
    reject: string[] = [],
    profileId?: string,
  ): Promise<CroMatch[]> =>
    (await api.post(`/cro/searches/${searchId}/confirm`, { clientProfileId, profileId, confirm, reject })).data,
};
